import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { apiUrl } from "../../../constant";
import { exportAuditDrilldownExcel } from "../auditDrilldownExport";

const BUCKET_LABELS = {
  in_progress: "In Progress",
  on_hold: "On Hold",
  not_started: "Ready to Start",
  tb_approved: "TB Approved",
  basic_analysis_started: "Basic Analysis Started",
  checklist_done: "Checklist Done",
  due_soon: "Due in 2 Days",
  overdue: "Overdue",
};

const AuditDrillDownModal = ({ isOpen, onClose, params, baseFilters }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState(null); // 'client_name' | 'assigned_to'
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortBy) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const av = (a?.[sortBy] || "").toString().toLowerCase();
      const bv = (b?.[sortBy] || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [data, sortBy, sortDir]);

  const sortIndicator = (field) => {
    if (sortBy !== field) return " ⇅";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  useEffect(() => {
    if (!isOpen || !params) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authtoken");
        const queryParams = {
          ...buildQuery(baseFilters),
          bucket: params.bucket,
          page,
          per_page: perPage,
        };
        if (params.manager_id != null) queryParams.manager_id = params.manager_id;
        if (params.phase_id != null) queryParams.phase_id = params.phase_id;
        if (params.phase_mode) queryParams.phase_mode = 1;

        const res = await axios.get(`${apiUrl}/audit-dashboard/drilldown`, {
          headers: { Authorization: `Bearer ${token}` },
          params: queryParams,
        });
        setData(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch (err) {
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, params, page, perPage, baseFilters]);

  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, params]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  // Phase mode = either an explicit phase_id was clicked, OR the response carries phase_name fields.
  const isPhaseMode = params?.phase_id != null || params?.phase_mode || data.some((r) => r?.phase_name != null);

  // Export every row for the currently-open bucket (all pages), honouring the same
  // filters/context the modal is already showing.
  const handleExport = async () => {
    if (exporting || !params?.bucket) return;
    const token = localStorage.getItem("authtoken");
    const baseParams = { ...buildQuery(baseFilters) };
    if (params.manager_id != null) baseParams.manager_id = params.manager_id;
    if (params.phase_id != null) baseParams.phase_id = params.phase_id;
    if (params.phase_mode) baseParams.phase_mode = 1;

    setExporting(true);
    try {
      const count = await exportAuditDrilldownExcel({
        authHeader: { Authorization: `Bearer ${token}` },
        baseParams,
        buckets: [params.bucket],
        isPhaseMode,
        filenameLabel: params.bucket,
      });
      if (count === 0) window.alert("No rows to export for this bucket.");
    } catch (err) {
      window.alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        Jobs — {BUCKET_LABELS[params?.bucket] || ""}
        {total > 0 && <small className="ml-2 text-muted">({total} rows)</small>}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center text-muted py-4">Loading…</div>
        ) : data.length === 0 ? (
          <div className="text-center text-muted py-4">No jobs in this bucket.</div>
        ) : (
          <Table responsive size="sm" striped>
            <thead>
              <tr>
                <th>Job ID</th>
                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("client_name")}
                >
                  Client{sortIndicator("client_name")}
                </th>
                <th>Sub Client</th>
                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("assigned_to")}
                >
                  Assigned To{sortIndicator("assigned_to")}
                </th>
                {isPhaseMode && <th>Phase</th>}
                <th>Job Type</th>
                <th>Status</th>
                <th>Assign Date</th>
                <th>Due Date</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr key={row.phase_row_id || row.task_id || idx}>
                  <td>{row.task_id || "—"}</td>
                  <td>{row.client_name || "—"}</td>
                  <td>{row.sub_client_name || "—"}</td>
                  <td>{row.assigned_to || "—"}</td>
                  {isPhaseMode && <td>{row.phase_name || "—"}</td>}
                  <td>{row.job_type || "—"}</td>
                  <td>
                    <Badge style={{ background: row.status_color || "#888", color: "#fff" }}>
                      {row.status_name || "—"}
                    </Badge>
                  </td>
                  <td>{formatDate(row.assigned_date)}</td>
                  <td>{formatDate(row.due_date)}</td>
                  <td>{row.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Pagination className="justify-content-end" size="sm">
            <PaginationItem disabled={page <= 1}>
              <PaginationLink onClick={() => setPage(page - 1)}>«</PaginationLink>
            </PaginationItem>
            <PaginationItem active>
              <PaginationLink>{page} / {totalPages}</PaginationLink>
            </PaginationItem>
            <PaginationItem disabled={page >= totalPages}>
              <PaginationLink onClick={() => setPage(page + 1)}>»</PaginationLink>
            </PaginationItem>
          </Pagination>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          size="sm"
          onClick={handleExport}
          disabled={loading || exporting || total === 0}
        >
          <i className={`fas ${exporting ? "fa-spinner fa-spin" : "fa-download"} mr-1`} />
          {exporting ? "Exporting…" : "Export to Excel"}
        </Button>
        <Button color="secondary" size="sm" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

function formatDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toISOString().slice(0, 10);
}

function buildQuery(filters) {
  if (!filters) return {};
  const out = {};
  if (filters.role?.value != null)  out.role              = filters.role.value;
  if (filters.client_types?.length) out.client_types      = filters.client_types.map((c) => c.value).join(",");
  if (filters.job_types?.length)    out.job_types         = filters.job_types.map((j) => j.value).join(",");
  if (filters.selected_user?.value) out.selected_user_id  = filters.selected_user.value;
  return out;
}

export default AuditDrillDownModal;
